import React, { useState, useContext, useEffect, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { ExcelImportPanel } from './ExcelImportPanel';
import { 
  Plus, Save, Download, Trash2, 
  Sun, Moon, Calendar, CheckCircle, AlertCircle,
  FileSpreadsheet, ChevronDown, ChevronRight, X, Package
} from 'lucide-react';

// Furnace definitions
const BIG_FURNACES = Array.from({ length: 17 }, (_, i) => `C${i + 1}`);
const SMALL_FURNACES = Array.from({ length: 7 }, (_, i) => `SM${i + 1}`);
const ALL_FURNACES = [...BIG_FURNACES, ...SMALL_FURNACES];

// Finished Goods Types
const FG_TYPES = ['Cubes', 'Notchbar', 'Shots', 'Ingots'];

function ProductionDataEntry() {
  const { API } = useContext(AppContext);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedShift, setSelectedShift] = useState('Day');
  const [productionData, setProductionData] = useState([]);
  const [furnaces, setFurnaces] = useState(ALL_FURNACES);
  const [furnacesLoaded, setFurnacesLoaded] = useState(false);
  const [availableScraps, setAvailableScraps] = useState([]); // From Raw Material with positive stock
  const [availableFG, setAvailableFG] = useState([]); // Available FG for melting (rejected)
  const [scrapTypesList, setScrapTypesList] = useState([]); // Unique scrap types with stock
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [expandedRows, setExpandedRows] = useState({}); // Track which rows are expanded

  // Generate batch number
  const generateBatchNumber = (date, shift, furnace) => {
    const dateStr = date.replace(/-/g, '');
    const shiftCode = shift === 'Day' ? 'D' : 'N';
    return `B${dateStr}-${shiftCode}-${furnace}`;
  };

  // Initialize empty row template with dynamic scrap inputs array and FG inputs array
  const createEmptyRow = (furnace) => ({
    id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    batchNumber: generateBatchNumber(selectedDate, selectedShift, furnace),
    date: selectedDate,
    shift: selectedShift,
    furnace: furnace,
    operatorNames: '',
    contractor: '',
    status: 'Open',
    // Dynamic scrap inputs - array of scrap entries
    scrapInputs: [],
    // Dynamic FG inputs - array of rejected FG for melting
    fgInputs: [],
    totalInput: 0,
    totalOutput: 0,
    recoveryPercent: 0,
    dross: 0,
    drossPercent: 0,
    burningLoss: 0,
    burningLossPercent: 0,
    // Multiple FG outputs - array instead of single value
    fgOutputs: [{ fgType: 'Cubes', quantity: 0 }],
    fgType: 'Cubes', // Legacy field for backwards compatibility
    remarks: ''
  });

  // Create empty scrap input entry
  const createEmptyScrapInput = () => ({
    id: `scrap-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    scrapType: '',
    weight: 0,
    partyName: '',
    invoiceNo: '',
    availableQty: 0,
    stockId: ''
  });

  // Create empty FG input entry (for rejected finished goods melting)
  const createEmptyFGInput = () => ({
    id: `fg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    category: '',
    weight: 0,
    availableQty: 0,
    source: 'Finished Goods Stock'
  });

  // Create empty FG output entry
  const createEmptyFGOutput = () => ({
    id: `fgout-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    fgType: 'Cubes',
    quantity: 0
  });

  // Get unique invoices for dropdown with aggregated available quantity
  const uniqueInvoices = useMemo(() => {
    const invoiceMap = {};
    availableScraps.forEach(s => {
      if (!invoiceMap[s.invoiceNo]) {
        invoiceMap[s.invoiceNo] = {
          invoiceNo: s.invoiceNo,
          partyName: s.partyName,
          materials: [],
          totalAvailableQty: 0  // Track total available qty across all materials
        };
      }
      invoiceMap[s.invoiceNo].materials.push({
        scrapType: s.scrapType,
        availableQty: s.availableQty,
        stockId: s.id
      });
      // Add this material's available qty to invoice total
      invoiceMap[s.invoiceNo].totalAvailableQty += (s.availableQty || 0);
    });
    return Object.values(invoiceMap);
  }, [availableScraps]);

  // Get materials for a specific invoice
  const getMaterialsForInvoice = (invoiceNo) => {
    return availableScraps.filter(s => s.invoiceNo === invoiceNo);
  };

  // Load available scraps from Raw Material Consumption & Stock (positive balance only)
  useEffect(() => {
    loadAvailableScraps();
    loadScrapTypes();
    loadAvailableFG();
  }, []);

  useEffect(() => {
    const loadFurnaces = async () => {
      try {
        const response = await axios.get(`${API}/master-data/production/furnaces`);
        const masterFurnaces = (response.data?.data || [])
          .map(item => item.furnaceNo?.trim())
          .filter(Boolean);

        if (masterFurnaces.length > 0) {
          setFurnaces([...new Set([...ALL_FURNACES, ...masterFurnaces])]);
        }
      } catch (error) {
        console.error('Error loading furnaces:', error);
      } finally {
        setFurnacesLoaded(true);
      }
    };

    loadFurnaces();
  }, [API]);

  // Load production data when date/shift changes
  useEffect(() => {
    if (!furnacesLoaded) return;
    loadProductionData();
  }, [selectedDate, selectedShift, furnacesLoaded, furnaces]);

  const loadAvailableScraps = async () => {
    try {
      // Only load lots with positive balance for new selections
      const response = await axios.get(`${API}/raw-material/available-scraps`);
      setAvailableScraps(response.data || []);
    } catch (error) {
      console.error('Error loading available scraps:', error);
      setAvailableScraps([]);
    }
  };

  const loadScrapTypes = async () => {
    try {
      const response = await axios.get(`${API}/raw-material/scrap-types-with-stock`, {
        
      });
      // Store unique scrap types for dropdown
      if (response.data.data) {
        const types = response.data.data.map(d => d.materialType);
        setScrapTypesList(types);
      }
    } catch (error) {
      console.error('Error loading scrap types:', error);
    }
  };

  const loadAvailableFG = async () => {
    try {
      const response = await axios.get(`${API}/dispatch/finished-goods/available-for-input`);
      setAvailableFG(response.data || []);
    } catch (error) {
      console.error('Error loading available FG:', error);
      setAvailableFG([]);
    }
  };

  const loadProductionData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/production/shift-entries`, {
        params: { date: selectedDate, shift: selectedShift },
        
      });
      if (response.data && response.data.length > 0) {
        // Migrate old data format to new format
        const migratedData = response.data.map(row => {
          // Ensure fgInputs array exists
          if (!row.fgInputs) {
            row.fgInputs = [];
          }
          // Migrate single fgType to fgOutputs array
          if (!row.fgOutputs || row.fgOutputs.length === 0) {
            const outputQty = parseFloat(row.totalOutput) || 0;
            row.fgOutputs = [{ 
              id: `fgout-${Date.now()}`,
              fgType: row.fgType || 'Cubes', 
              quantity: outputQty 
            }];
          }
          return row;
        });
        const existingFurnaces = new Set(migratedData.map(row => row.furnace));
        const missingRows = furnaces
          .filter(furnace => !existingFurnaces.has(furnace))
          .map(createEmptyRow);
        setProductionData([...migratedData, ...missingRows]);
      } else {
        const initialData = furnaces.map(furnace => createEmptyRow(furnace));
        setProductionData(initialData);
      }
    } catch (error) {
      const initialData = furnaces.map(furnace => createEmptyRow(furnace));
      setProductionData(initialData);
    }
    setLoading(false);
  };

  // Calculate derived values for a row
  const calculateDerivedValues = (row) => {
    // Calculate scrap input total
    const scrapInputTotal = row.scrapInputs.reduce((sum, scrap) => sum + (parseFloat(scrap.weight) || 0), 0);
    
    // Calculate FG input total (rejected finished goods for melting)
    const fgInputTotal = (row.fgInputs || []).reduce((sum, fg) => sum + (parseFloat(fg.weight) || 0), 0);
    
    // Total input = Scrap + FG inputs
    const totalInput = scrapInputTotal + fgInputTotal;
    
    // Calculate total output from multiple FG outputs
    let totalOutput = 0;
    if (row.fgOutputs && row.fgOutputs.length > 0) {
      totalOutput = row.fgOutputs.reduce((sum, out) => sum + (parseFloat(out.quantity) || 0), 0);
    } else {
      totalOutput = parseFloat(row.totalOutput) || 0;
    }
    
    const dross = parseFloat(row.dross) || 0;
    
    const burningLoss = totalInput - totalOutput - dross;
    const recoveryPercent = totalInput > 0 ? ((totalOutput / totalInput) * 100).toFixed(1) : 0;
    const drossPercent = totalInput > 0 ? ((dross / totalInput) * 100).toFixed(1) : 0;
    const burningLossPercent = totalInput > 0 ? ((burningLoss / totalInput) * 100).toFixed(1) : 0;

    // Set primary FG type from first output for backwards compatibility
    const primaryFgType = row.fgOutputs && row.fgOutputs.length > 0 ? row.fgOutputs[0].fgType : row.fgType;

    return {
      ...row,
      totalInput,
      totalOutput,
      recoveryPercent: parseFloat(recoveryPercent),
      drossPercent: parseFloat(drossPercent),
      burningLoss,
      burningLossPercent: parseFloat(burningLossPercent),
      fgType: primaryFgType
    };
  };

  // Toggle row expansion
  const toggleRowExpansion = (furnace) => {
    setExpandedRows(prev => ({
      ...prev,
      [furnace]: !prev[furnace]
    }));
  };

  // Add scrap input to a furnace
  const addScrapInput = (rowIndex) => {
    const updatedData = [...productionData];
    updatedData[rowIndex].scrapInputs.push(createEmptyScrapInput());
    setProductionData(updatedData);
    // Auto-expand the row when adding scrap
    setExpandedRows(prev => ({ ...prev, [updatedData[rowIndex].furnace]: true }));
  };

  // Remove scrap input from a furnace
  const removeScrapInput = (rowIndex, scrapIndex) => {
    const updatedData = [...productionData];
    updatedData[rowIndex].scrapInputs.splice(scrapIndex, 1);
    updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
    setProductionData(updatedData);
  };

  // Add FG input (rejected finished goods for melting)
  const addFGInput = (rowIndex) => {
    const updatedData = [...productionData];
    if (!updatedData[rowIndex].fgInputs) {
      updatedData[rowIndex].fgInputs = [];
    }
    updatedData[rowIndex].fgInputs.push(createEmptyFGInput());
    setProductionData(updatedData);
    setExpandedRows(prev => ({ ...prev, [updatedData[rowIndex].furnace]: true }));
  };

  // Remove FG input
  const removeFGInput = (rowIndex, fgIndex) => {
    const updatedData = [...productionData];
    updatedData[rowIndex].fgInputs.splice(fgIndex, 1);
    updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
    setProductionData(updatedData);
  };

  // Update FG input field
  const updateFGInput = (rowIndex, fgIndex, field, value) => {
    const updatedData = [...productionData];
    
    if (field === 'category') {
      const selectedFG = availableFG.find(fg => fg.category === value);
      if (selectedFG) {
        updatedData[rowIndex].fgInputs[fgIndex].category = value;
        updatedData[rowIndex].fgInputs[fgIndex].availableQty = selectedFG.availableQty;
      }
    } else if (field === 'weight') {
      const category = updatedData[rowIndex].fgInputs[fgIndex].category;
      const currentFG = availableFG.find(fg => fg.category === category);
      const availableQty = currentFG ? currentFG.availableQty : 0;
      const enteredWeight = parseFloat(value) || 0;
      
      if (enteredWeight > availableQty && availableQty > 0) {
        alert(`Warning: Maximum available ${category} stock is ${availableQty.toLocaleString()} kg`);
        updatedData[rowIndex].fgInputs[fgIndex].weight = availableQty;
      } else {
        updatedData[rowIndex].fgInputs[fgIndex].weight = enteredWeight;
      }
    } else {
      updatedData[rowIndex].fgInputs[fgIndex][field] = value;
    }
    
    updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
    setProductionData(updatedData);
  };

  // Add FG output
  const addFGOutput = (rowIndex) => {
    const updatedData = [...productionData];
    if (!updatedData[rowIndex].fgOutputs) {
      updatedData[rowIndex].fgOutputs = [];
    }
    updatedData[rowIndex].fgOutputs.push(createEmptyFGOutput());
    setProductionData(updatedData);
  };

  // Remove FG output
  const removeFGOutput = (rowIndex, outputIndex) => {
    const updatedData = [...productionData];
    if (updatedData[rowIndex].fgOutputs.length > 1) {
      updatedData[rowIndex].fgOutputs.splice(outputIndex, 1);
      updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
      setProductionData(updatedData);
    }
  };

  // Update FG output field
  const updateFGOutput = (rowIndex, outputIndex, field, value) => {
    const updatedData = [...productionData];
    if (!updatedData[rowIndex].fgOutputs) {
      updatedData[rowIndex].fgOutputs = [{ fgType: 'Cubes', quantity: 0 }];
    }
    updatedData[rowIndex].fgOutputs[outputIndex][field] = field === 'quantity' ? (parseFloat(value) || 0) : value;
    updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
    setProductionData(updatedData);
  };

  // Update scrap input field
  const updateScrapInput = (rowIndex, scrapIndex, field, value) => {
    const updatedData = [...productionData];
    const currentScrap = updatedData[rowIndex].scrapInputs[scrapIndex];
    
    // If invoice is selected, auto-fill party name and find available materials
    if (field === 'invoiceNo') {
      const selectedScrap = availableScraps.find(s => s.invoiceNo === value);
      if (selectedScrap) {
        updatedData[rowIndex].scrapInputs[scrapIndex].invoiceNo = value;
        updatedData[rowIndex].scrapInputs[scrapIndex].partyName = selectedScrap.partyName;
        // Auto-select material type if only one material for this invoice
        const materialsForInvoice = availableScraps.filter(s => s.invoiceNo === value);
        if (materialsForInvoice.length === 1) {
          updatedData[rowIndex].scrapInputs[scrapIndex].scrapType = materialsForInvoice[0].scrapType;
          updatedData[rowIndex].scrapInputs[scrapIndex].availableQty = materialsForInvoice[0].availableQty;
          updatedData[rowIndex].scrapInputs[scrapIndex].stockId = materialsForInvoice[0].id;
        } else {
          // Clear material type if multiple materials exist for this invoice
          updatedData[rowIndex].scrapInputs[scrapIndex].scrapType = '';
          updatedData[rowIndex].scrapInputs[scrapIndex].availableQty = 0;
          updatedData[rowIndex].scrapInputs[scrapIndex].stockId = '';
        }
      }
    } else if (field === 'scrapType') {
      // When material type is selected, update available quantity
      const invoiceNo = currentScrap.invoiceNo;
      const selectedItem = availableScraps.find(s => s.invoiceNo === invoiceNo && s.scrapType === value);
      if (selectedItem) {
        updatedData[rowIndex].scrapInputs[scrapIndex].scrapType = value;
        updatedData[rowIndex].scrapInputs[scrapIndex].availableQty = selectedItem.availableQty;
        updatedData[rowIndex].scrapInputs[scrapIndex].stockId = selectedItem.id;
      } else {
        updatedData[rowIndex].scrapInputs[scrapIndex][field] = value;
      }
    } else if (field === 'weight') {
      // STRICT validation: Weight CANNOT exceed available balance
      const invoiceNo = currentScrap.invoiceNo;
      const scrapType = currentScrap.scrapType;
      
      // Look up current balance from availableScraps
      const currentMaterial = invoiceNo && scrapType 
        ? availableScraps.find(s => s.invoiceNo === invoiceNo && s.scrapType === scrapType)
        : null;
      const currentAvailableQty = currentMaterial ? currentMaterial.availableQty : 0;
      
      const enteredWeight = parseFloat(value) || 0;
      
      if (!invoiceNo || !scrapType) {
        // Invoice or material not selected yet
        alert('Please select Invoice and Material Type first before entering weight.');
        updatedData[rowIndex].scrapInputs[scrapIndex].weight = 0;
      } else if (currentAvailableQty === 0) {
        // Lot is exhausted - don't allow any consumption
        alert(`❌ STOCK EXHAUSTED\n\nThis consignment is fully consumed.\nNo more material can be used from:\n\nInvoice: ${invoiceNo}\nMaterial: ${scrapType}\n\nPlease select a different consignment.`);
        updatedData[rowIndex].scrapInputs[scrapIndex].weight = 0;
      } else if (enteredWeight > currentAvailableQty) {
        // BLOCK entry - do not allow exceeding available balance
        alert(`❌ INSUFFICIENT STOCK\n\nYou cannot consume more than the available balance.\n\nInvoice: ${invoiceNo}\nMaterial: ${scrapType}\nAvailable: ${currentAvailableQty.toLocaleString()} kg\nEntered: ${enteredWeight.toLocaleString()} kg\n\nPlease enter a quantity ≤ ${currentAvailableQty.toLocaleString()} kg`);
        // Set to maximum available instead of allowing the invalid value
        updatedData[rowIndex].scrapInputs[scrapIndex].weight = currentAvailableQty;
      } else {
        updatedData[rowIndex].scrapInputs[scrapIndex].weight = enteredWeight;
      }
      
      // Update the scrap's availableQty to reflect current balance
      updatedData[rowIndex].scrapInputs[scrapIndex].availableQty = currentAvailableQty;
    } else {
      updatedData[rowIndex].scrapInputs[scrapIndex][field] = value;
    }
    
    updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
    setProductionData(updatedData);
  };

  // Update row field
  const updateRowField = (rowIndex, field, value) => {
    const updatedData = [...productionData];
    updatedData[rowIndex][field] = value;
    updatedData[rowIndex] = calculateDerivedValues(updatedData[rowIndex]);
    setProductionData(updatedData);
  };

  // Save all data
  const handleSaveAll = async () => {
    // Pre-save validation: Check all scrap inputs don't exceed available stock
    const validationErrors = [];
    
    for (const row of productionData) {
      for (const scrap of row.scrapInputs || []) {
        if (scrap.weight > 0 && scrap.invoiceNo && scrap.scrapType) {
          const currentMaterial = availableScraps.find(
            s => s.invoiceNo === scrap.invoiceNo && s.scrapType === scrap.scrapType
          );
          const availableQty = currentMaterial ? currentMaterial.availableQty : 0;
          
          if (scrap.weight > availableQty) {
            validationErrors.push(
              `${row.furnace}: ${scrap.scrapType} (${scrap.invoiceNo}) - Entered: ${scrap.weight.toLocaleString()} kg, Available: ${availableQty.toLocaleString()} kg`
            );
          }
        }
      }
    }
    
    if (validationErrors.length > 0) {
      alert(`❌ CANNOT SAVE - STOCK VALIDATION FAILED\n\nThe following entries exceed available stock:\n\n${validationErrors.join('\n')}\n\nPlease correct these entries before saving.`);
      return;
    }
    
    setSaveStatus('saving');
    try {
      await axios.post(`${API}/production/shift-entries`, {
        date: selectedDate,
        shift: selectedShift,
        entries: productionData
      });
      setSaveStatus('saved');
      // Reload available scraps to get updated balances after consumption
      loadAvailableScraps();
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Save error:', error);
      // Show detailed error message for stock validation
      if (error.response?.data?.detail) {
        alert(`❌ SAVE FAILED\n\n${error.response.data.detail}`);
      }
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const rows = [];
    rows.push(['Batch No', 'Furnace', 'Operators', 'Contractor', 'Status', 'Scrap Type', 'Weight (kg)', 'Party Name', 'Invoice No', 'Total Input', 'Output', 'Recovery %', 'Dross', 'Dross %', 'B.Loss', 'B.Loss %', 'FG Type']);
    
    productionData.forEach(row => {
      if (row.scrapInputs.length === 0) {
        rows.push([
          row.batchNumber, row.furnace, row.operatorNames, row.contractor || '', row.status,
          '', '', '', '',
          row.totalInput, row.totalOutput, row.recoveryPercent,
          row.dross, row.drossPercent, row.burningLoss, row.burningLossPercent, row.fgType
        ]);
      } else {
        row.scrapInputs.forEach((scrap, idx) => {
          rows.push([
            idx === 0 ? row.batchNumber : '',
            idx === 0 ? row.furnace : '',
            idx === 0 ? row.operatorNames : '',
            idx === 0 ? (row.contractor || '') : '',
            idx === 0 ? row.status : '',
            scrap.scrapType, scrap.weight, scrap.partyName, scrap.invoiceNo,
            idx === 0 ? row.totalInput : '',
            idx === 0 ? row.totalOutput : '',
            idx === 0 ? row.recoveryPercent : '',
            idx === 0 ? row.dross : '',
            idx === 0 ? row.drossPercent : '',
            idx === 0 ? row.burningLoss : '',
            idx === 0 ? row.burningLossPercent : '',
            idx === 0 ? row.fgType : ''
          ]);
        });
      }
    });
    
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `production_${selectedDate}_${selectedShift}.csv`;
    a.click();
  };

  // Summary calculations
  const activeFurnaces = productionData.filter(r => r.status !== 'Closed' && r.totalInput > 0);
  const totalInputAll = productionData.reduce((sum, r) => sum + (r.totalInput || 0), 0);
  const totalOutputAll = productionData.reduce((sum, r) => sum + (parseFloat(r.totalOutput) || 0), 0);
  const avgRecovery = totalInputAll > 0 ? ((totalOutputAll / totalInputAll) * 100).toFixed(1) : 0;

  // Scrap type breakdown
  const scrapBreakdown = {};
  productionData.forEach(row => {
    row.scrapInputs.forEach(scrap => {
      if (scrap.scrapType && scrap.weight > 0) {
        scrapBreakdown[scrap.scrapType] = (scrapBreakdown[scrap.scrapType] || 0) + parseFloat(scrap.weight);
      }
    });
  });

  return (
    <div className="space-y-4" data-testid="production-data-entry-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg">
            <FileSpreadsheet className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Production Data Entry</h1>
            <p className="text-sm text-gray-500">Shift-wise tracking with dynamic scrap inputs & invoice linking</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Date Picker */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
            <Calendar size={16} className="text-gray-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border-none outline-none text-sm font-medium"
              data-testid="date-picker"
            />
          </div>

          {/* Shift Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['Day', 'Night'].map((shift) => (
              <button
                key={shift}
                onClick={() => setSelectedShift(shift)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-1 ${
                  selectedShift === shift
                    ? 'bg-white text-orange-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`shift-${shift.toLowerCase()}`}
              >
                {shift === 'Day' ? <Sun size={14} /> : <Moon size={14} />}
                {shift}
              </button>
            ))}
          </div>

          {/* Export & Save */}
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
            data-testid="export-csv-btn"
          >
            <Download size={16} />
            Export CSV
          </button>

          <button
            onClick={handleSaveAll}
            disabled={saveStatus === 'saving'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
              saveStatus === 'saved' 
                ? 'bg-green-500 text-white' 
                : saveStatus === 'error'
                ? 'bg-red-500 text-white'
                : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
            }`}
            data-testid="save-all-btn"
          >
            {saveStatus === 'saving' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Saving...
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <CheckCircle size={16} />
                Saved!
              </>
            ) : saveStatus === 'error' ? (
              <>
                <AlertCircle size={16} />
                Error
              </>
            ) : (
              <>
                <Save size={16} />
                Save All
              </>
            )}
          </button>
        </div>
      </div>

      {/* Excel Import / Export */}
      <ExcelImportPanel selectedDate={selectedDate} selectedShift={selectedShift} onImported={() => { loadProductionData(); loadAvailableScraps(); }} />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Active Furnaces</p>
          <p className="text-2xl font-bold">{activeFurnaces.length}</p>
          <p className="text-xs opacity-70">of 22</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Input</p>
          <p className="text-2xl font-bold">{(totalInputAll / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Total Output</p>
          <p className="text-2xl font-bold">{(totalOutputAll / 1000).toFixed(1)} T</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Avg Recovery</p>
          <p className="text-2xl font-bold">{avgRecovery}%</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
          <p className="text-xs opacity-80">Batch ID</p>
          <p className="text-lg font-bold truncate">
            {generateBatchNumber(selectedDate, selectedShift, 'ALL').substring(0, 12)}...
          </p>
        </div>
      </div>

      {/* Scrap Mix Summary */}
      {Object.keys(scrapBreakdown).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">Scrap Mix (Batch Percentage)</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(scrapBreakdown).map(([scrapType, weight]) => (
              <div key={scrapType} className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                <span className="font-medium text-blue-700">{scrapType}</span>
                <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-bold">
                  {weight.toLocaleString()} kg ({totalInputAll > 0 ? ((weight / totalInputAll) * 100).toFixed(1) : 0}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Production Table with Expandable Rows */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-3 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-red-50 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
            <FileSpreadsheet size={16} className="text-orange-500" />
            Furnace-wise Production Data (Click row to expand & add scraps)
          </h3>
          <span className="text-xs text-gray-500">
            {selectedShift} Shift: {selectedShift === 'Day' ? '6:00 AM - 6:00 PM' : '6:00 PM - 6:00 AM'}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-gray-700 w-8"></th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Batch #</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Furnace</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap min-w-[120px]">Operators</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap min-w-[100px]">Contractor</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">Status</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">Scraps</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-900 bg-blue-50 whitespace-nowrap">Total In</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[70px]">Output</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-900 bg-green-50 whitespace-nowrap">REC%</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-700 whitespace-nowrap min-w-[60px]">Dross</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">D%</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-700 whitespace-nowrap">B.Loss</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">BL%</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">FG</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-500 border-t-transparent"></div>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : (
                productionData.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    {/* Main Row */}
                    <tr 
                      className={`hover:bg-orange-50/50 transition cursor-pointer ${row.status === 'Closed' ? 'bg-gray-50 opacity-60' : ''}`}
                      data-testid={`row-${row.furnace}`}
                    >
                      {/* Expand Toggle */}
                      <td className="px-2 py-2">
                        <button
                          onClick={() => toggleRowExpansion(row.furnace)}
                          className="p-1 hover:bg-gray-200 rounded transition"
                        >
                          {expandedRows[row.furnace] ? (
                            <ChevronDown size={14} className="text-gray-500" />
                          ) : (
                            <ChevronRight size={14} className="text-gray-500" />
                          )}
                        </button>
                      </td>

                      {/* Batch Number */}
                      <td className="px-2 py-2">
                        <span className="font-mono text-[10px] text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                          {row.batchNumber}
                        </span>
                      </td>

                      {/* Furnace */}
                      <td className="px-2 py-2">
                        <span className={`font-bold ${row.furnace.startsWith('C') ? 'text-blue-600' : 'text-purple-600'}`}>
                          {row.furnace}
                        </span>
                      </td>

                      {/* Operators */}
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={row.operatorNames}
                          onChange={(e) => updateRowField(rowIndex, 'operatorNames', e.target.value)}
                          placeholder="Operator names"
                          className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-orange-400 focus:ring-1 focus:ring-orange-200 outline-none"
                          disabled={row.status === 'Closed'}
                        />
                      </td>

                      {/* Contractor */}
                      <td className="px-1 py-1">
                        <input
                          type="text"
                          value={row.contractor || ''}
                          onChange={(e) => updateRowField(rowIndex, 'contractor', e.target.value)}
                          placeholder="Contractor name"
                          className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:border-orange-400 focus:ring-1 focus:ring-orange-200 outline-none"
                          disabled={row.status === 'Closed'}
                        />
                      </td>

                      {/* Status */}
                      <td className="px-1 py-1">
                        <select
                          value={row.status}
                          onChange={(e) => updateRowField(rowIndex, 'status', e.target.value)}
                          className={`w-full px-1 py-1 border rounded text-xs outline-none ${
                            row.status === 'Closed' ? 'bg-gray-100 text-gray-500' : 'bg-green-50 text-green-700 border-green-200'
                          }`}
                        >
                          <option value="Open">Open</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>

                      {/* Scraps Count */}
                      <td className="px-2 py-2 text-center">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => toggleRowExpansion(row.furnace)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition text-[10px] font-medium"
                            disabled={row.status === 'Closed'}
                          >
                            <Plus size={10} />
                            {row.scrapInputs.length > 0 ? `${row.scrapInputs.length} scrap` : 'Add'}
                          </button>
                          {row.fgInputs && row.fgInputs.length > 0 && (
                            <span className="text-[9px] text-purple-600 font-medium">+{row.fgInputs.length} FG</span>
                          )}
                        </div>
                      </td>

                      {/* Total Input */}
                      <td className="px-2 py-2 bg-blue-50 text-right font-bold text-blue-700">
                        {row.totalInput > 0 ? row.totalInput.toLocaleString() : '-'}
                      </td>

                      {/* Output - Now shows total from multiple FG outputs */}
                      <td className="px-2 py-2 text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-bold text-green-700">
                            {row.totalOutput > 0 ? row.totalOutput.toLocaleString() : '-'}
                          </span>
                          {row.fgOutputs && row.fgOutputs.length > 1 && (
                            <span className="text-[9px] text-green-500">{row.fgOutputs.length} types</span>
                          )}
                        </div>
                      </td>

                      {/* Recovery % */}
                      <td className={`px-2 py-2 text-center font-bold ${
                        row.recoveryPercent >= 90 ? 'bg-green-100 text-green-700' :
                        row.recoveryPercent >= 80 ? 'bg-yellow-100 text-yellow-700' :
                        row.recoveryPercent > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-50 text-gray-400'
                      }`}>
                        {row.recoveryPercent > 0 ? `${row.recoveryPercent}%` : '-'}
                      </td>

                      {/* Dross */}
                      <td className="px-1 py-1">
                        <input
                          type="number"
                          min="0"
                          value={row.dross || ''}
                          onChange={(e) => updateRowField(rowIndex, 'dross', e.target.value)}
                          placeholder="0"
                          className="w-full px-1 py-1 border border-gray-200 rounded text-xs text-right focus:border-yellow-400 outline-none"
                          disabled={row.status === 'Closed'}
                        />
                      </td>

                      {/* Dross % */}
                      <td className="px-2 py-2 text-center text-yellow-600 text-[10px]">
                        {row.drossPercent > 0 ? `${row.drossPercent}%` : '-'}
                      </td>

                      {/* Burning Loss */}
                      <td className="px-2 py-2 text-right text-red-600 font-medium">
                        {row.burningLoss !== 0 ? row.burningLoss.toLocaleString() : '-'}
                      </td>

                      {/* Burning Loss % */}
                      <td className="px-2 py-2 text-center text-red-600 text-[10px]">
                        {row.burningLossPercent !== 0 ? `${row.burningLossPercent}%` : '-'}
                      </td>

                      {/* FG Type - Show summary of multiple outputs */}
                      <td className="px-1 py-1">
                        <div className="text-[10px]">
                          {row.fgOutputs && row.fgOutputs.length > 0 ? (
                            row.fgOutputs.map((out, idx) => (
                              <div key={idx} className={`${idx > 0 ? 'border-t border-gray-200 pt-0.5 mt-0.5' : ''}`}>
                                <span className="font-medium text-orange-600">{out.fgType}</span>
                                <span className="text-gray-500 ml-1">({(out.quantity || 0).toLocaleString()})</span>
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-400">{row.fgType || 'Cubes'}</span>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Scrap Inputs */}
                    {expandedRows[row.furnace] && (
                      <tr className="bg-gray-50">
                        <td colSpan={15} className="px-4 py-3">
                          <div className="space-y-4">
                            {/* SCRAP INPUTS SECTION */}
                            <div className="bg-white rounded-lg border border-gray-200 p-3">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                  Scrap Inputs for {row.furnace}
                                </span>
                                <button
                                  onClick={() => addScrapInput(rowIndex)}
                                  className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition"
                                  disabled={row.status === 'Closed'}
                                >
                                  <Plus size={12} />
                                  Add Scrap
                                </button>
                              </div>

                              {row.scrapInputs.length === 0 ? (
                                <div className="text-center py-3 text-gray-400 text-xs">
                                  No scraps added. Click "Add Scrap" to add inputs.
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-gray-600 px-2">
                                    <div className="col-span-3">Invoice No (Select)</div>
                                    <div className="col-span-2">Material Type</div>
                                    <div className="col-span-2">Party Name</div>
                                    <div className="col-span-2 text-right">Available (kg)</div>
                                    <div className="col-span-2 text-right">Weight (kg)</div>
                                    <div className="col-span-1"></div>
                                  </div>
                                  
                                  {row.scrapInputs.map((scrap, scrapIndex) => {
                                    const materialsForInvoice = scrap.invoiceNo ? getMaterialsForInvoice(scrap.invoiceNo) : [];
                                    const currentMaterial = scrap.invoiceNo && scrap.scrapType 
                                      ? availableScraps.find(s => s.invoiceNo === scrap.invoiceNo && s.scrapType === scrap.scrapType)
                                      : null;
                                    const availableQty = currentMaterial ? currentMaterial.availableQty : 0;
                                    const enteredWeight = parseFloat(scrap.weight) || 0;
                                    const isOverLimit = enteredWeight > availableQty && availableQty > 0;
                                    const currentInvoiceInList = scrap.invoiceNo && uniqueInvoices.some(inv => inv.invoiceNo === scrap.invoiceNo);
                                    
                                    return (
                                    <div key={scrap.id} className={`grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded border ${isOverLimit ? 'border-red-400 bg-red-50' : 'border-gray-200'}`}>
                                      <div className="col-span-3">
                                        <select
                                          value={scrap.invoiceNo}
                                          onChange={(e) => updateScrapInput(rowIndex, scrapIndex, 'invoiceNo', e.target.value)}
                                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:border-blue-400 outline-none"
                                        >
                                          <option value="">Select Invoice...</option>
                                          {scrap.invoiceNo && !currentInvoiceInList && (
                                            <option value={scrap.invoiceNo} disabled>{scrap.invoiceNo} (Exhausted)</option>
                                          )}
                                          {uniqueInvoices.map(inv => (
                                            <option key={inv.invoiceNo} value={inv.invoiceNo}>
                                              {inv.invoiceNo} - {inv.partyName} ({inv.totalAvailableQty?.toLocaleString() || 0} kg)
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-span-2">
                                        <select
                                          value={scrap.scrapType}
                                          onChange={(e) => updateScrapInput(rowIndex, scrapIndex, 'scrapType', e.target.value)}
                                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs outline-none"
                                          disabled={!scrap.invoiceNo}
                                        >
                                          <option value="">{scrap.invoiceNo ? 'Select Material...' : 'Select Invoice first'}</option>
                                          {materialsForInvoice.map(mat => (
                                            <option key={mat.scrapType} value={mat.scrapType}>{mat.scrapType} ({mat.availableQty.toLocaleString()} kg)</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-span-2">
                                        <input type="text" value={scrap.partyName} readOnly placeholder="Auto-filled" className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-gray-100" />
                                      </div>
                                      <div className="col-span-2">
                                        <div className={`w-full px-2 py-1.5 border rounded text-xs text-right font-medium ${availableQty > 0 ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-500'}`}>
                                          {availableQty > 0 ? availableQty.toLocaleString() : 'EXHAUSTED'}
                                        </div>
                                      </div>
                                      <div className="col-span-2">
                                        <input 
                                          type="number" 
                                          min="0" 
                                          max={availableQty > 0 ? availableQty : 0}
                                          value={scrap.weight || ''} 
                                          onChange={(e) => updateScrapInput(rowIndex, scrapIndex, 'weight', e.target.value)} 
                                          placeholder="0" 
                                          className={`w-full px-2 py-1.5 border rounded text-xs text-right outline-none ${isOverLimit ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-300'}`}
                                          disabled={availableQty === 0}
                                          title={availableQty === 0 ? 'Stock exhausted - cannot enter weight' : `Maximum: ${availableQty.toLocaleString()} kg`}
                                        />
                                        {isOverLimit && (
                                          <p className="text-[9px] text-red-600 mt-0.5 font-semibold">⚠️ Exceeds limit! Max: {availableQty.toLocaleString()}</p>
                                        )}
                                      </div>
                                      <div className="col-span-1 flex justify-end">
                                        <button onClick={() => removeScrapInput(rowIndex, scrapIndex)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 size={14} /></button>
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* FINISHED GOODS INPUTS SECTION (Rejected FG for Melting) */}
                            <div className="bg-white rounded-lg border border-purple-200 p-3">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-semibold text-purple-700 flex items-center gap-2">
                                  <Package size={14} />
                                  Rejected Finished Goods (for Melting)
                                  <span className="text-[10px] text-purple-500 font-normal">(Optional - add rejected FG to melt)</span>
                                </span>
                                <button
                                  onClick={() => addFGInput(rowIndex)}
                                  className="flex items-center gap-1 px-2 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600 transition"
                                  disabled={row.status === 'Closed'}
                                >
                                  <Plus size={12} />
                                  Add FG
                                </button>
                              </div>

                              {(!row.fgInputs || row.fgInputs.length === 0) ? (
                                <div className="text-center py-3 text-purple-300 text-xs">
                                  No rejected FG added. Use this for melting rejected finished goods.
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-purple-600 px-2">
                                    <div className="col-span-4">FG Category</div>
                                    <div className="col-span-3 text-right">Available Stock (kg)</div>
                                    <div className="col-span-4 text-right">Weight to Melt (kg)</div>
                                    <div className="col-span-1"></div>
                                  </div>
                                  
                                  {row.fgInputs.map((fgInput, fgIndex) => {
                                    const currentFG = availableFG.find(fg => fg.category === fgInput.category);
                                    const fgAvailableQty = currentFG ? currentFG.availableQty : 0;
                                    
                                    return (
                                    <div key={fgInput.id} className="grid grid-cols-12 gap-2 items-center bg-purple-50 p-2 rounded border border-purple-200">
                                      <div className="col-span-4">
                                        <select
                                          value={fgInput.category}
                                          onChange={(e) => updateFGInput(rowIndex, fgIndex, 'category', e.target.value)}
                                          className="w-full px-2 py-1.5 border border-purple-300 rounded text-xs focus:border-purple-400 outline-none"
                                        >
                                          <option value="">Select FG Type...</option>
                                          {availableFG.map(fg => (
                                            <option key={fg.category} value={fg.category}>
                                              {fg.name} ({fg.availableQty.toLocaleString()} kg available)
                                            </option>
                                          ))}
                                          {/* Also show all FG types even if no stock */}
                                          {FG_TYPES.filter(t => !availableFG.find(fg => fg.category === t)).map(t => (
                                            <option key={t} value={t} disabled>{t} (0 kg available)</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="col-span-3">
                                        <div className={`w-full px-2 py-1.5 border rounded text-xs text-right font-medium ${fgAvailableQty > 0 ? 'bg-purple-100 border-purple-200 text-purple-700' : 'bg-gray-100 text-gray-400'}`}>
                                          {fgAvailableQty > 0 ? fgAvailableQty.toLocaleString() : '0'}
                                        </div>
                                      </div>
                                      <div className="col-span-4">
                                        <input
                                          type="number"
                                          min="0"
                                          max={fgAvailableQty}
                                          value={fgInput.weight || ''}
                                          onChange={(e) => updateFGInput(rowIndex, fgIndex, 'weight', e.target.value)}
                                          placeholder="0"
                                          className="w-full px-2 py-1.5 border border-purple-300 rounded text-xs text-right outline-none focus:border-purple-400"
                                        />
                                      </div>
                                      <div className="col-span-1 flex justify-end">
                                        <button onClick={() => removeFGInput(rowIndex, fgIndex)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 size={14} /></button>
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* MULTIPLE FG OUTPUTS SECTION */}
                            <div className="bg-white rounded-lg border border-green-200 p-3">
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-xs font-semibold text-green-700 flex items-center gap-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  Finished Goods Output
                                  <span className="text-[10px] text-green-500 font-normal">(Multiple FG types can be produced in same shift)</span>
                                </span>
                                <button
                                  onClick={() => addFGOutput(rowIndex)}
                                  className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600 transition"
                                  disabled={row.status === 'Closed'}
                                >
                                  <Plus size={12} />
                                  Add FG Type
                                </button>
                              </div>

                              <div className="space-y-2">
                                <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-green-600 px-2">
                                  <div className="col-span-5">FG Type</div>
                                  <div className="col-span-6 text-right">Output Quantity (kg)</div>
                                  <div className="col-span-1"></div>
                                </div>
                                
                                {(row.fgOutputs || [{ fgType: 'Cubes', quantity: row.totalOutput || 0 }]).map((output, outputIndex) => (
                                  <div key={output.id || outputIndex} className="grid grid-cols-12 gap-2 items-center bg-green-50 p-2 rounded border border-green-200">
                                    <div className="col-span-5">
                                      <select
                                        value={output.fgType}
                                        onChange={(e) => updateFGOutput(rowIndex, outputIndex, 'fgType', e.target.value)}
                                        className="w-full px-2 py-1.5 border border-green-300 rounded text-xs focus:border-green-400 outline-none"
                                        disabled={row.status === 'Closed'}
                                      >
                                        {FG_TYPES.map(fg => (
                                          <option key={fg} value={fg}>{fg}</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="col-span-6">
                                      <input
                                        type="number"
                                        min="0"
                                        value={output.quantity || ''}
                                        onChange={(e) => updateFGOutput(rowIndex, outputIndex, 'quantity', e.target.value)}
                                        placeholder="0"
                                        className="w-full px-2 py-1.5 border border-green-300 rounded text-xs text-right outline-none focus:border-green-400 font-medium"
                                        disabled={row.status === 'Closed'}
                                      />
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                      {(row.fgOutputs || []).length > 1 && (
                                        <button onClick={() => removeFGOutput(rowIndex, outputIndex)} className="p-1.5 text-red-500 hover:bg-red-100 rounded"><Trash2 size={14} /></button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                
                                {/* Total Output Summary */}
                                <div className="flex justify-end pt-2 border-t border-green-200">
                                  <div className="text-xs font-bold text-green-700">
                                    Total Output: {row.totalOutput?.toLocaleString() || 0} kg
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}

              {/* Totals Row */}
              {!loading && productionData.length > 0 && (
                <tr className="bg-gray-100 font-bold">
                  <td className="px-2 py-2" colSpan={6}>TOTAL</td>
                  <td className="px-2 py-2 text-right bg-blue-100 text-blue-800">
                    {totalInputAll.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-right text-green-700">
                    {totalOutputAll.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center bg-green-100 text-green-800">
                    {avgRecovery}%
                  </td>
                  <td className="px-2 py-2 text-right text-yellow-700">
                    {productionData.reduce((sum, r) => sum + (parseFloat(r.dross) || 0), 0).toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-yellow-700">
                    {totalInputAll > 0 ? ((productionData.reduce((sum, r) => sum + (parseFloat(r.dross) || 0), 0) / totalInputAll) * 100).toFixed(1) : 0}%
                  </td>
                  <td className="px-2 py-2 text-right text-red-700">
                    {productionData.reduce((sum, r) => sum + (r.burningLoss || 0), 0).toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-red-700">
                    {totalInputAll > 0 ? ((productionData.reduce((sum, r) => sum + (r.burningLoss || 0), 0) / totalInputAll) * 100).toFixed(1) : 0}%
                  </td>
                  <td className="px-2 py-2"></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <h4 className="font-semibold mb-2">How to use:</h4>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li><strong>Click the arrow</strong> next to any furnace to expand and see input/output sections</li>
          <li><strong>Scrap Inputs</strong>: Select Invoice from dropdown - Material Type and Party Name auto-fill</li>
          <li><strong>Rejected FG</strong>: Optionally add rejected finished goods (Cubes, Ingots, etc.) for melting</li>
          <li><strong>Multiple FG Outputs</strong>: You can produce multiple FG types (e.g., Cubes + Notchbar) in the same shift</li>
          <li><strong>Total Input</strong> = Scrap + Rejected FG; <strong>Recovery %</strong> and <strong>Burning Loss</strong> are auto-calculated</li>
          <li>Click <strong>Export CSV</strong> to download or <strong>Save All</strong> to persist</li>
        </ul>
        <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg">
          <p className="text-yellow-800 text-xs font-semibold">
            ⚠️ STOCK VALIDATION: You cannot consume more than the available balance for any consignment. 
            The system will prevent saving if any entry exceeds available stock.
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProductionDataEntry;
